import { useEffect, useMemo, useRef } from 'react'
import useMosInfo from '../../../common/provider/useMosInfo'
import { getEffectivePlannedByTechnologyIds } from '../utils/plannedJobTabs'

const sameIds = (left, right) => left.length === right.length && left.every((id, index) => id === right[index])

/**
 * Story 193 (MIC-4346, RN6): shares the technology selection of the "All Jobs" tab between every
 * widget that opens PlannedJobCustomModal -- Planned Jobs and Operation -- exactly as
 * `useSharedVisibleTabs` shares the visible tabs. The selection is a decision of the WORK CENTRE:
 * choosing it from one widget's configuration panel holds for both.
 *
 * The shared value lives in `globalData.plannedJobListPlannedByTechnologyIds` and is mirrored back
 * into each widget's own `data.plannedByTechnologyIds`, which is what persists in
 * widgets/device/{id}.
 *
 * Loading writes nothing (S22, RN6.4): the mirror compares effective values, so a config without the
 * field, or with technologies deactivated since, is left exactly as saved until the configurator
 * changes the selector.
 */
const useSharedPlannedByTechnologyIds = (data, setData) => {
  const { globalData, setGlobalData } = useMosInfo()
  const initRef = useRef(false)

  // RN6.2: a selection with technologies beats an empty one whatever the mount order of the two
  // widgets; if both carry a different non-empty selection, the first to mount wins and the next
  // change settles it for both (same rule as the visible tabs).
  useEffect(() => {
    if (initRef.current) return
    initRef.current = true
    const own = getEffectivePlannedByTechnologyIds(data?.plannedByTechnologyIds)
    setGlobalData((prev) => {
      const shared = prev?.plannedJobListPlannedByTechnologyIds
      if (!Array.isArray(shared)) return { ...prev, plannedJobListPlannedByTechnologyIds: own }
      if (shared.length === 0 && own.length > 0) return { ...prev, plannedJobListPlannedByTechnologyIds: own }
      return prev
    })
  }, [setGlobalData, data])

  useEffect(() => {
    const shared = globalData?.plannedJobListPlannedByTechnologyIds
    if (!Array.isArray(shared)) return
    const current = getEffectivePlannedByTechnologyIds(data?.plannedByTechnologyIds)
    if (sameIds(getEffectivePlannedByTechnologyIds(shared), current)) return
    if (setData) setData({ ...(data || {}), plannedByTechnologyIds: shared })
  }, [globalData?.plannedJobListPlannedByTechnologyIds, data, setData])

  // Memoized on the CONTENT of the list, never on its identity, so a polling tick that rebuilds the
  // array does not look like a selection change downstream.
  const source = Array.isArray(globalData?.plannedJobListPlannedByTechnologyIds)
    ? globalData.plannedJobListPlannedByTechnologyIds
    : data?.plannedByTechnologyIds
  const contentKey = getEffectivePlannedByTechnologyIds(source).join('|')
  return useMemo(() => (contentKey === '' ? [] : contentKey.split('|').map(Number)), [contentKey])
}

export default useSharedPlannedByTechnologyIds
